import { useNavigate } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Notifications, WavingHand } from "@mui/icons-material";

import { useAlerts, useAuth, useLayout } from "features/store";

import { forwardRef, useEffect, useMemo } from "react";
import { IconButton, Tooltip } from "@mui/material";

interface HeaderProps {
  title?: string;
}

// Both forms spelled out: suffixing an "s" gets "mismatchs" wrong.
const pluralise = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

// The alert counts sit inline beside the bell rather than in a Badge: a badge is
// absolutely positioned, so a two-part count grew outside the icon and overlapped
// whatever sat next to it in the toolbar. forwardRef so each pill can carry its
// own Tooltip - the colour alone does not say which count is which.
const AlertCountPill = forwardRef<
  HTMLSpanElement,
  { count: number; color: "primary" | "error" }
>(({ count, color, ...tooltipProps }, ref) => (
  <Box
    ref={ref}
    component="span"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    minWidth={20}
    height={20}
    px={0.75}
    borderRadius={10}
    fontSize={12}
    fontWeight={600}
    lineHeight={1}
    // contrastText, not a hardcoded white: the theme resolves the light-blue
    // primary to dark text because white on it fails the contrast check.
    sx={{
      bgcolor: `${color}.main`,
      color: `${color}.contrastText`,
    }}
    {...tooltipProps}
  >
    {count}
  </Box>
));

AlertCountPill.displayName = "AlertCountPill";

const Header = ({ title }: HeaderProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { trade_signal_count, position_mismatch_count } = useAlerts();
  const navigate = useNavigate();

  const { drawerWidth } = useLayout();
  const environment = (process.env.NODE_ENV || "").toLowerCase();
  const isDevelopmentEnvironment = environment === "development";
  // Development gets a dark bar so it is obvious at a glance which deployment
  // you are looking at. Everywhere else the bar keeps the theme's own AppBar
  // background and must not be overridden: "background-color: inherit" resolved
  // to the transparent #root div, and the bar is position: fixed, so page
  // content scrolled straight through it - on /report the tab list rode up over
  // the page title.
  const headerBackgroundColor = isDevelopmentEnvironment
    ? "#003057"
    : undefined;
  const headerTextColor = isDevelopmentEnvironment ? "white" : "#003057";

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  const tradeSignalLabel = pluralise(
    trade_signal_count,
    "unseen trade signal",
    "unseen trade signals",
  );
  const mismatchLabel = pluralise(
    position_mismatch_count,
    "position mismatch",
    "position mismatches",
  );

  const alertsDescription = useMemo(() => {
    const parts: string[] = [];
    if (trade_signal_count) {
      parts.push(tradeSignalLabel);
    }
    if (position_mismatch_count) {
      parts.push(mismatchLabel);
    }

    return parts.length ? parts.join(", ") : "No unseen alerts";
  }, [
    trade_signal_count,
    position_mismatch_count,
    tradeSignalLabel,
    mismatchLabel,
  ]);

  return (
    <AppBar sx={{ backgroundColor: headerBackgroundColor }}>
      <Toolbar>
        <Typography
          noWrap
          variant="h5"
          marginLeft={`${drawerWidth}px`}
          color={headerTextColor}
        >
          {title == "Home" ? (
            <>
              {`Hello ${user ? user.first_name : ""}`}
              <WavingHand sx={{ color: "orange", ml: 1 }} />
            </>
          ) : (
            title
          )}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          flexGrow={1}
        >
          {/* One tooltip per child rather than one on the button: hovering a pill
              should say which count it is - the colour alone does not.
              describeChild keeps the button's own accessible name. */}
          <IconButton
            href="/alerts"
            color="inherit"
            aria-label={`Alerts: ${alertsDescription}`}
            sx={{ gap: 0.75, px: 1.25, borderRadius: 5 }}
          >
            <Tooltip title={alertsDescription} describeChild>
              <Notifications
                sx={{ color: headerTextColor, display: "block" }}
              />
            </Tooltip>
            {!!trade_signal_count && (
              <Tooltip title={tradeSignalLabel} describeChild>
                <AlertCountPill count={trade_signal_count} color="primary" />
              </Tooltip>
            )}
            {!!position_mismatch_count && (
              <Tooltip title={mismatchLabel} describeChild>
                <AlertCountPill count={position_mismatch_count} color="error" />
              </Tooltip>
            )}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
